import type React from 'react'
import type { PropsWithChildren, ReactNode } from 'react'

import { PromptSection } from '@/components/molecules/proposal-builder/editor/styles'
import { cn } from '@/utils/shadcn/utils'

interface ContainerProps {
  children: ReactNode
}

export const ProposalAccountDetailsContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex h-0 flex-grow w-full">{children}</div>

export const ProposalAccountDetailsHeaderContainer: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <div className="flex justify-between items-center w-full border-b border-primary-border px-4 py-[6px] h-[36px]">
    {children}
  </div>
)

export const ProposalAccountDetailsMainContent: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex flex-col h-full flex-1">{children}</div>

export const ProposalAccountDetailsContentLayout: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <div className="flex flex-row h-full relative overflow-hidden">
    {children}
  </div>
)

export const ProposalAccountDetailsContentContainer: React.FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div
    className={cn(
      'flex flex-col h-full flex-1 transition-[padding] duration-500 ease-in-out',
      className,
    )}
  >
    {children}
  </div>
)

export const AccountMetadataContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex flex-col gap-1.25">{children}</div>

export const AccountNameAndManagementTypeContainer: React.FC<
  PropsWithChildren
> = ({ children }) => <div className="flex flex-row gap-1.5">{children}</div>

interface IconContainerProps extends PropsWithChildren {
  onClick?: (e: React.MouseEvent) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  role?: string
  tabIndex?: number
  'aria-label'?: string
}

export const IconContainer: React.FC<IconContainerProps> = ({
  children,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  'aria-label': ariaLabel,
}) => (
  // biome-ignore lint/a11y/noStaticElementInteractions: Bulk suppression as part of biome migration
  // biome-ignore lint/a11y/useAriaPropsSupportedByRole: Bulk suppression as part of biome migration
  <div
    className="flex flex-row p-[6px] cursor-pointer border border-primary-border rounded-md items-center hover:bg-moment-white-200 dark:hover:bg-moment-grey-800"
    onClick={onClick}
    onKeyDown={onKeyDown}
    role={role}
    tabIndex={tabIndex}
    aria-label={ariaLabel}
  >
    {children}
  </div>
)

export const NavigationContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex flex-row gap-2 items-center">{children}</div>

export const ContentContainer: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col w-full @container/content h-0 flex-grow overflow-y-auto">
    {children}
  </div>
)

export const BeforeAndAfterContainer: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex p-4 gap-2 w-full flex-col @[898px]/content:flex-row h-fit">
    {children}
  </div>
)

export const SelectPortfolioOverlayContainer: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex mx-4 my-3 h-full bg-tertiary-background items-center align-middle justify-center rounded-[6px]">
    {children}
  </div>
)

export const PortfolioAllocationContainer: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex mt-4 mx-4 pt-2 pb-1 px-1 gap-2 items-start flex-col border border-primary-border rounded-md bg-tertiary-background">
    {children}
  </div>
)

export const PortfolioOverviewContainer: React.FC<
  PropsWithChildren<{ spanFullWidth?: boolean }>
> = ({ children, spanFullWidth = false }) => (
  <div
    className={cn(
      'flex pt-2 pb-1 px-1 gap-2 items-start w-full flex-col  h-fit border border-primary-border rounded-md bg-tertiary-background',
      spanFullWidth ? '@[898px]/content:w-full' : '@[898px]/content:w-1/2',
    )}
  >
    {children}
  </div>
)

export const PortfolioHeaderContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex items-center px-[12px] w-full">{children}</div>

export const SwatchAndTitleContainer: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="flex flex-grow gap-2 items-center">{children}</div>

export const ColorSwatch = ({ color }: { color?: string }) => (
  <div className={`h-2 w-2 rounded-[2px]`} style={{ backgroundColor: color }} />
)

export const SectionContainer: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col w-full gap-1">{children}</div>
)

export const AccountMetadataAndSleeveSelectorContainer: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <div className="flex flex-row justify-between w-full items-center px-4 pt-3 pb-[6px]">
    {children}
  </div>
)

export const AccountHoldingsContainer = ({ children }: ContainerProps) => (
  <div className="flex-1 w-full h-full flex flex-col">{children}</div>
)

export const HoldingsBorderContainer = ({ children }: ContainerProps) => (
  <div className="border-b border-primary-border max-w-full">{children}</div>
)

export const SearchContainer = ({ children }: ContainerProps) => (
  <div className="p-[12px] gap-2 max-w-[225px]">{children}</div>
)

export const PromptSectionContainer = ({
  prompt,
  showPrompt,
  handleClick,
  handleKeyDown,
}: {
  prompt: string | null
  showPrompt: boolean
  handleClick: () => void
  handleKeyDown: (e: React.KeyboardEvent) => void
}) => (
  <div className="w-full h-fit p-4 pb-0">
    <div className="flex flex-col w-full border border-primary-border rounded-md p-3">
      <PromptSection
        prompt={prompt}
        showPrompt={showPrompt}
        handleClick={handleClick}
        handleKeyDown={handleKeyDown}
        showIcon
      />
    </div>
  </div>
)

export const VerticalSeparator = () => {
  return <div className="h-[18px] w-[1px] bg-primary-border" />
}
